import * as React from 'react';
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
} from 'firebase/auth';
import { Navigate } from 'react-router-dom';
import { ArrowLeft, Check, Eye, EyeOff } from 'lucide-react';
import { auth } from '../lib/firebase';
import { useUser } from './AuthProvider';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { cn } from '../lib/utils';
import logoLight from '../assets/logo_light.png';

type Step = 'welcome' | 'signin' | 'signup';

export function SignInPage() {
  const { user, loading } = useUser();
  const [step, setStep] = React.useState<Step>('welcome');
  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  function resetMessages() {
    setError(null);
  }

  function go(next: Step) {
    resetMessages();
    setStep(next);
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      setError(humanizeFirebaseError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (fullName.trim()) {
        try {
          await updateProfile(cred.user, { displayName: fullName.trim() });
        } catch {
          /* non-blocking */
        }
      }
      try {
        await sendEmailVerification(cred.user);
      } catch {
        /* best-effort — surfaced later by the in-app banner */
      }
    } catch (err) {
      setError(humanizeFirebaseError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    setBusy(true);
    setError(null);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      setError(humanizeFirebaseError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen w-full bg-background">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col overflow-hidden md:flex-row md:items-stretch md:p-6 md:gap-6">
        <BrandPanel step={step} />

        <section className="relative flex flex-1 flex-col justify-center bg-card px-6 py-10 md:rounded-3xl md:px-12 md:py-14 md:shadow-sm">
          {step !== 'welcome' && (
            <button
              type="button"
              onClick={() => go('welcome')}
              className="absolute left-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:left-6 md:top-6"
              aria-label="Zurück"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}

          {step === 'welcome' && (
            <WelcomeForm onSignIn={() => go('signin')} onSignUp={() => go('signup')} onGoogle={handleGoogle} busy={busy} error={error} />
          )}

          {step === 'signin' && (
            <SignInForm
              email={email}
              password={password}
              showPassword={showPassword}
              busy={busy}
              error={error}
              onEmail={setEmail}
              onPassword={setPassword}
              onToggleShow={() => setShowPassword((v) => !v)}
              onSubmit={handleSignIn}
              onSwitch={() => go('signup')}
            />
          )}

          {step === 'signup' && (
            <SignUpForm
              fullName={fullName}
              email={email}
              password={password}
              confirm={confirm}
              showPassword={showPassword}
              showConfirm={showConfirm}
              isValidEmail={isValidEmail}
              busy={busy}
              error={error}
              onFullName={setFullName}
              onEmail={setEmail}
              onPassword={setPassword}
              onConfirm={setConfirm}
              onToggleShow={() => setShowPassword((v) => !v)}
              onToggleShowConfirm={() => setShowConfirm((v) => !v)}
              onSubmit={handleSignUp}
              onSwitch={() => go('signin')}
            />
          )}
        </section>
      </div>
    </div>
  );
}

function BrandPanel({ step }: { step: Step }) {
  const heading =
    step === 'signin' ? 'Willkommen zurück' : step === 'signup' ? 'Konto erstellen' : '...griassdy!';
  const phonetic = step === 'welcome' ? '[ˈg̊ʀiə̯s di]' : null;
  const sub =
    step === 'signin'
      ? 'Schön dich wiederzusehen.'
      : step === 'signup'
        ? 'In wenigen Schritten startklar.'
        : 'Digitaler-Agent für dein Telefon.';

  return (
    <aside
      className={cn(
        'relative flex shrink-0 flex-col justify-between overflow-hidden text-primary-foreground',
        'bg-gradient-to-br from-primary via-primary to-accent',
        'px-6 py-10 md:rounded-3xl md:px-10 md:py-12',
        'md:w-[42%] md:min-h-[640px]',
      )}
    >
      <DecorativeShapes />

      <div className="relative z-10 flex items-center gap-3">
        <img src={logoLight} alt="griassdy" className="h-[6.5em] w-auto select-none" draggable={false} />
      </div>

      <div className="relative z-10 mt-8 md:mt-0">
        <h1 className="text-3xl font-semibold leading-tight tracking-tight md:text-4xl">{heading}</h1>
        {phonetic && (
          <p className="mt-1 font-mono text-sm text-primary-foreground/70 md:text-base">{phonetic}</p>
        )}
        <p className="mt-2 text-sm text-primary-foreground/80 md:text-base">{sub}</p>
      </div>

      <div className="relative z-10 hidden md:block">
        <p className="text-xs uppercase tracking-widest text-primary-foreground/60">cp&middot;3 voice agent</p>
      </div>
    </aside>
  );
}

function DecorativeShapes() {
  return (
    <>
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary-foreground/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-primary-foreground/10 blur-3xl" />
      <div className="pointer-events-none absolute right-8 bottom-8 h-24 w-24 rounded-full border border-primary-foreground/15" />
    </>
  );
}

function WelcomeForm({
  onSignIn,
  onSignUp,
  onGoogle,
  busy,
  error,
}: {
  onSignIn: () => void;
  onSignUp: () => void;
  onGoogle: () => void;
  busy: boolean;
  error: string | null;
}) {
  return (
    <div className="mx-auto flex w-full max-w-sm flex-col gap-6">
      <div className="space-y-1.5 text-center">
        <h2 className="text-2xl font-semibold tracking-tight">Willkommen</h2>
        <p className="text-sm text-muted-foreground">Melde dich an oder erstelle ein neues Konto.</p>
      </div>

      <div className="flex flex-col gap-3">
        <Button type="button" size="lg" className="w-full" onClick={onSignIn}>
          Anmelden
        </Button>
        <Button type="button" size="lg" variant="outline" className="w-full" onClick={onSignUp}>
          Registrieren
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs uppercase tracking-wider text-muted-foreground">oder</span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <Button type="button" variant="secondary" className="w-full" onClick={onGoogle} disabled={busy}>
        Mit Google fortfahren
      </Button>

      {error && <p className="text-center text-sm text-destructive">{error}</p>}
    </div>
  );
}

function SignInForm(props: {
  email: string;
  password: string;
  showPassword: boolean;
  busy: boolean;
  error: string | null;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onToggleShow: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onSwitch: () => void;
}) {
  return (
    <form onSubmit={props.onSubmit} className="mx-auto flex w-full max-w-sm flex-col gap-5">
      <div className="space-y-1">
        <h2 className="text-2xl font-semibold tracking-tight">Hallo, anmelden</h2>
        <p className="text-sm text-muted-foreground">Melde dich mit deiner E-Mail-Adresse an.</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="email">E-Mail</Label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          required
          value={props.email}
          onChange={(e) => props.onEmail(e.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password">Passwort</Label>
        <PasswordInput
          id="password"
          autoComplete="current-password"
          required
          minLength={8}
          value={props.password}
          show={props.showPassword}
          onToggle={props.onToggleShow}
          onChange={(e) => props.onPassword(e.target.value)}
        />
      </div>

      {props.error && <p className="text-sm text-destructive">{props.error}</p>}

      <Button type="submit" size="lg" className="w-full" disabled={props.busy}>
        Anmelden
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        Noch kein Konto?{' '}
        <button type="button" onClick={props.onSwitch} className="font-medium text-primary hover:underline">
          Registrieren
        </button>
      </p>
    </form>
  );
}

function SignUpForm(props: {
  fullName: string;
  email: string;
  password: string;
  confirm: string;
  showPassword: boolean;
  showConfirm: boolean;
  isValidEmail: boolean;
  busy: boolean;
  error: string | null;
  onFullName: (v: string) => void;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  onConfirm: (v: string) => void;
  onToggleShow: () => void;
  onToggleShowConfirm: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onSwitch: () => void;
}) {
  const passwordsMatch = props.password.length > 0 && props.password === props.confirm;
  return (
    <form onSubmit={props.onSubmit} className="mx-auto flex w-full max-w-sm flex-col gap-5">
      <div className="space-y-1">
        <h2 className="text-2xl font-semibold tracking-tight">Konto erstellen</h2>
        <p className="text-sm text-muted-foreground">Lege ein neues Konto an, um loszulegen.</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="full-name">Vollständiger Name</Label>
        <Input
          id="full-name"
          type="text"
          autoComplete="name"
          required
          value={props.fullName}
          onChange={(e) => props.onFullName(e.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="signup-email">E-Mail</Label>
        <div className="relative">
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            required
            value={props.email}
            onChange={(e) => props.onEmail(e.target.value)}
            className="pr-10"
          />
          {props.isValidEmail && (
            <Check className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="signup-password">Passwort</Label>
        <PasswordInput
          id="signup-password"
          autoComplete="new-password"
          required
          minLength={8}
          value={props.password}
          show={props.showPassword}
          onToggle={props.onToggleShow}
          onChange={(e) => props.onPassword(e.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="signup-confirm">Passwort bestätigen</Label>
        <PasswordInput
          id="signup-confirm"
          autoComplete="new-password"
          required
          minLength={8}
          value={props.confirm}
          show={props.showConfirm}
          onToggle={props.onToggleShowConfirm}
          onChange={(e) => props.onConfirm(e.target.value)}
          trailing={
            passwordsMatch ? <Check className="h-4 w-4 text-primary" /> : null
          }
        />
      </div>

      {props.error && <p className="text-sm text-destructive">{props.error}</p>}

      <Button type="submit" size="lg" className="w-full" disabled={props.busy}>
        Registrieren
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        Bereits ein Konto?{' '}
        <button type="button" onClick={props.onSwitch} className="font-medium text-primary hover:underline">
          Anmelden
        </button>
      </p>
    </form>
  );
}

interface PasswordInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  show: boolean;
  onToggle: () => void;
  trailing?: React.ReactNode;
}

function PasswordInput({ show, onToggle, trailing, className, ...rest }: PasswordInputProps) {
  return (
    <div className="relative">
      <Input type={show ? 'text' : 'password'} className={cn('pr-20', className)} {...rest} />
      <div className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
        {trailing && <span className="pointer-events-none mr-1">{trailing}</span>}
        <button
          type="button"
          onClick={onToggle}
          tabIndex={-1}
          aria-label={show ? 'Passwort verbergen' : 'Passwort anzeigen'}
          className="pointer-events-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

function humanizeFirebaseError(err: unknown): string {
  const code = (err as { code?: string }).code ?? '';
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'E-Mail oder Passwort sind falsch.';
    case 'auth/user-not-found':
      return 'Kein Konto mit dieser E-Mail.';
    case 'auth/email-already-in-use':
      return 'Diese E-Mail ist bereits registriert.';
    case 'auth/weak-password':
      return 'Passwort zu schwach (min. 8 Zeichen).';
    case 'auth/popup-closed-by-user':
      return 'Anmeldung abgebrochen.';
    default:
      return (err as Error).message ?? 'Unbekannter Fehler';
  }
}
